import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import api from '../../api';

const MESES = [
  '',
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre'
];

const TAB_FILTROS = [
  { id: '', label: 'Todos' },
  { id: 'sin_datos', label: 'Falta datos SII' },
  { id: 'sin_informe', label: 'Falta informe' },
  { id: 'revision', label: 'Revisar' },
  { id: 'listo', label: 'Listos' },
  { id: 'error', label: 'Problemas' }
];

const ESTADO_STYLE = {
  sin_datos: 'bg-slate-100 text-slate-800 border-slate-200',
  sin_informe: 'bg-sky-50 text-sky-900 border-sky-200',
  procesando: 'bg-amber-50 text-amber-900 border-amber-200',
  revision: 'bg-orange-50 text-orange-900 border-orange-200',
  listo: 'bg-emerald-50 text-emerald-900 border-emerald-200',
  error: 'bg-red-50 text-red-900 border-red-200'
};

const RUN_TERMINAL = new Set([
  'READY',
  'WAITING_APPROVAL',
  'BLOCKED',
  'FAILED',
  'CANCELLED',
  'SENT',
  'APPROVED'
]);

const RUN_STATUS_ES = {
  QUEUED: 'En cola en el servidor',
  PENDING: 'Iniciando',
  SYNCING: 'Descargando ventas/compras del SII (5–15 min)',
  VALIDATING_DATA: 'Validando datos',
  CALCULATING: 'Calculando IVA',
  VALIDATING_RESULT: 'Revisando resultado',
  GENERATING: 'Generando PDF e informe'
};

function mesAnioCalendario(date = new Date()) {
  return { mes: date.getMonth() + 1, anio: date.getFullYear() };
}

const fmtMonto = (n) => {
  if (n == null || n === '') return '—';
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0
  }).format(n);
};

function datosResumen(row) {
  const v = row.rcvFilas?.ventas || 0;
  const c = row.rcvFilas?.compras || 0;
  if (v + c === 0) return 'Aún no hay resumen RCV para este mes';
  return `Ventas: ${v} tipos DTE · Compras: ${c} tipos DTE`;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function formatElapsed(startMs) {
  if (!startMs) return '0:00';
  const s = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

const PIPELINE_STEPS = [
  { id: 'sync', label: 'Datos SII', statuses: ['SYNCING'] },
  { id: 'valid', label: 'Validar', statuses: ['VALIDATING_DATA', 'VALIDATING_RESULT'] },
  { id: 'calc', label: 'Calcular IVA', statuses: ['CALCULATING'] },
  { id: 'gen', label: 'Informe / PDF', statuses: ['GENERATING'] },
  { id: 'done', label: 'Listo', statuses: ['READY', 'WAITING_APPROVAL', 'APPROVED', 'SENT'] }
];

function stepIndexForStatus(status) {
  if (!status) return 0;
  if (['QUEUED', 'PENDING'].includes(status)) return 0;
  for (let i = 0; i < PIPELINE_STEPS.length; i++) {
    if (PIPELINE_STEPS[i].statuses.includes(status)) return i;
  }
  if (RUN_TERMINAL.has(status)) return PIPELINE_STEPS.length - 1;
  return 0;
}

export default function PanelInformesIva() {
  const [{ mes, anio }, setPeriodo] = useState(() => mesAnioCalendario());
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [tab, setTab] = useState('');
  const [busyCliente, setBusyCliente] = useState(null);
  const [job, setJob] = useState(null);
  const [clock, setClock] = useState(Date.now());
  const [drawer, setDrawer] = useState(null);
  const [detalleRun, setDetalleRun] = useState(null);
  const [configCliente, setConfigCliente] = useState(null);
  const [globalConfig, setGlobalConfig] = useState(null);
  const [adminOpen, setAdminOpen] = useState(false);
  const [digest, setDigest] = useState(null);
  const [auditLog, setAuditLog] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [deliveries, setDeliveries] = useState([]);

  const loadDashboard = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get(`/informes-iva/dashboard/${anio}/${mes}`);
      setDashboard(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [anio, mes]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (!job || job.done) return undefined;
    const t = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(t);
  }, [job]);

  const nombreCliente = useCallback(
    (clienteId) => {
      const it = dashboard?.items?.find((i) => String(i.cliente._id) === String(clienteId));
      return it?.cliente?.nombre || 'Empresa';
    },
    [dashboard]
  );

  const updateJobFromRun = (run) => {
    if (!run) return;
    setJob((j) =>
      j
        ? {
            ...j,
            status: run.status,
            logs: run.logEntries || [],
            iva: run.estimatedVatDetermined ?? j.iva,
            startedAtMs: run.startedAt ? new Date(run.startedAt).getTime() : j.startedAtMs
          }
        : j
    );
  };

  const pollRunUntilDone = async (runId, maxMs = 1200000) => {
    const start = Date.now();
    while (Date.now() - start < maxMs) {
      const { data } = await api.get(`/informes-iva/run/${runId}?lite=1`);
      updateJobFromRun(data.run);
      if (data.run?.status && RUN_TERMINAL.has(data.run.status)) {
        return data;
      }
      await sleep(2000);
    }
    throw new Error(
      'Sigue procesando en el servidor. Cierre este aviso y pulse Actualizar lista en unos minutos, o abra Más opciones → Ver informe si ya apareció listo.'
    );
  };

  const verPreview = (runId) => {
    const url = `${window.location.origin}/informes-iva/ver/${runId}`;
    window.open(url, '_blank', 'noopener');
  };

  const finishRunJob = async (runId, runStatus) => {
    await loadDashboard(true);
    if (['READY', 'WAITING_APPROVAL', 'APPROVED', 'SENT'].includes(runStatus)) {
      setJob((j) => ({
        ...j,
        done: true,
        success: true,
        status: runStatus,
        runId
      }));
      toast.success('Informe generado — abriendo vista…');
      verPreview(runId);
      return;
    }
    setJob((j) => ({ ...j, done: true, success: false, status: runStatus, runId }));
    try {
      const { data } = await api.get(`/informes-iva/run/${runId}`);
      setDetalleRun(data);
    } catch {
      /* */
    }
    toast.error('No se pudo completar el informe');
  };

  const items = useMemo(() => {
    let list = dashboard?.items || [];
    const q = filtro.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (it) =>
          (it.cliente.nombre || '').toLowerCase().includes(q) ||
          (it.cliente.rut || '').toLowerCase().includes(q) ||
          (it.cliente.asignado || '').toLowerCase().includes(q)
      );
    }
    if (tab) list = list.filter((it) => it.estado?.key === tab);
    return list;
  }, [dashboard, filtro, tab]);

  const pollPeriodSource = async (clienteId, maxMs = 1200000) => {
    const start = Date.now();
    while (Date.now() - start < maxMs) {
      const { data } = await api.get(`/informes-iva/period-source/${clienteId}/${anio}/${mes}`);
      const v = data.periodSource?.ventas?.resumenFilas ?? data.raw?.ventasResumenFilas ?? 0;
      const c = data.periodSource?.compras?.resumenFilas ?? data.raw?.comprasResumenFilas ?? 0;
      setJob((j) =>
        j
          ? {
              ...j,
              logs: data.syncInProgress
                ? [`⏳ SII en curso… ventas ${v} filas · compras ${c} filas · ${formatElapsed(j.startedAtMs)}`]
                : [`✅ Sync terminado: ventas ${v} · compras ${c} filas`]
            }
          : j
      );
      if (!data.syncInProgress) return data;
      await sleep(3000);
    }
    throw new Error('El SII sigue respondiendo lento. Actualice la lista más tarde.');
  };

  const traerDatosSii = async (clienteId, { f29 = false } = {}) => {
    setBusyCliente(clienteId);
    setJob({
      kind: 'sync',
      clienteId,
      nombre: nombreCliente(clienteId),
      startedAtMs: Date.now(),
      status: 'SYNCING',
      logs: ['Iniciando descarga en el SII…'],
      done: false
    });
    try {
      await api.post(`/informes-iva/sync/${clienteId}`, { mes, anio, f29 });
      await pollPeriodSource(clienteId);
      setJob((j) => ({ ...j, done: true, success: true, status: 'DONE' }));
      toast.success('Datos del SII guardados');
      await loadDashboard(true);
    } catch (e) {
      setJob((j) => ({ ...j, done: true, success: false, error: e.message }));
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusyCliente(null);
    }
  };

  const generarInforme = async (clienteId, { autoSync = false } = {}) => {
    setBusyCliente(clienteId);
    const nombre = nombreCliente(clienteId);
    setJob({
      kind: 'run',
      clienteId,
      nombre,
      runId: null,
      startedAtMs: Date.now(),
      status: autoSync ? 'SYNCING' : 'CALCULATING',
      logs: [autoSync ? 'Iniciando sync SII + informe…' : 'Generando informe con datos guardados…'],
      done: false,
      autoSync
    });
    try {
      const body = {
        mes,
        anio,
        generatePdf: true,
        autoSync,
        wait: !autoSync
      };
      const { data: accepted } = await api.post(`/informes-iva/run/${clienteId}`, body);
      const runId = accepted.runId;
      if (!runId) throw new Error('Sin respuesta del servidor');

      setJob((j) => ({ ...j, runId: String(runId) }));

      if (accepted.alreadyRunning) {
        toast.info('Ya había un proceso en curso — mostrando el avance real');
      }

      if (accepted.async || body.wait !== true) {
        const result = await pollRunUntilDone(runId);
        await finishRunJob(runId, result.run?.status);
        return;
      }

      const st = accepted.status;
      updateJobFromRun({
        status: st,
        logEntries: [`Estado final: ${st}`],
        estimatedVatDetermined: accepted.ivaDeterminado
      });
      await finishRunJob(runId, st);
    } catch (e) {
      setJob((j) => ({ ...j, done: true, success: false, error: e.message }));
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setBusyCliente(null);
    }
  };

  const verDetalleRun = async (runId) => {
    try {
      const { data } = await api.get(`/informes-iva/run/${runId}`);
      setDetalleRun(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const descargarPdf = async (runId) => {
    try {
      const res = await api.get(`/informes-iva/pdf/${runId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `informe-iva-${runId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo descargar PDF');
    }
  };

  const aprobar = async (runId) => {
    try {
      await api.post(`/informes-iva/approve/${runId}`);
      toast.success('Informe aprobado');
      await loadDashboard(true);
      setDrawer(null);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const enviar = async (runId) => {
    if (!window.confirm('¿Enviar el informe al email configurado del cliente?')) return;
    try {
      await api.post(`/informes-iva/send/${runId}`);
      toast.success('Informe enviado');
      await loadDashboard(true);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const abrirDrawer = (row) => {
    setDrawer(row);
    api.get(`/informes-iva/historial/${row.cliente._id}`).then(({ data }) => setHistorial(data || []));
  };

  const abrirConfig = async (clienteId) => {
    try {
      const [{ data }, { data: del }] = await Promise.all([
        api.get(`/informes-iva/config/cliente/${clienteId}`),
        api.get(`/informes-iva/deliveries/${clienteId}`)
      ]);
      setConfigCliente({ ...data, clienteId });
      setDeliveries(del || []);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const guardarConfig = async () => {
    if (!configCliente) return;
    try {
      await api.put(`/informes-iva/config/cliente/${configCliente.clienteId}`, {
        activoInformes: configCliente.activoInformes,
        emailCliente: configCliente.emailCliente,
        notaContador: configCliente.notaContador,
        remanenteManual: configCliente.remanenteManual,
        ppmPorcentaje: configCliente.ppmPorcentaje,
        ppmMontoManual: configCliente.ppmMontoManual,
        autoSendEnabled: configCliente.autoSendEnabled,
        modoEnvio: configCliente.modoEnvio,
        approvalRequired: configCliente.approvalRequired,
        frecuencia: configCliente.frecuencia,
        ventanaDatos: configCliente.ventanaDatos
      });
      toast.success('Configuración guardada');
      setConfigCliente(null);
      loadDashboard(true);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const abrirConfigGlobal = async () => {
    try {
      const { data } = await api.get('/informes-iva/config/global');
      setGlobalConfig(data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const guardarConfigGlobal = async () => {
    if (!globalConfig) return;
    try {
      await api.put('/informes-iva/config/global', {
        digestMode: globalConfig.digestMode,
        emailOficina: globalConfig.emailOficina,
        frescuraHoras: globalConfig.frescuraHoras,
        autoSyncOnRun: globalConfig.autoSyncOnRun,
        syncF29OnRun: globalConfig.syncF29OnRun,
        scheduledRunsEnabled: globalConfig.scheduledRunsEnabled,
        autoSendGlobalEnabled: globalConfig.autoSendGlobalEnabled,
        permitirRemanenteCeroAsumido: globalConfig.permitirRemanenteCeroAsumido,
        smtpHost: globalConfig.smtpHost,
        smtpPort: globalConfig.smtpPort,
        smtpUser: globalConfig.smtpUser,
        smtpFrom: globalConfig.smtpFrom,
        smtpPass: globalConfig.smtpPass
      });
      toast.success('Configuración global guardada');
      setGlobalConfig(null);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    }
  };

  const primaryButton = (row) => {
    const id = row.cliente._id;
    const busy = busyCliente === id;
    const e = row.estado?.key;
    const run = row.lastRun;

    if (busy || e === 'procesando') {
      return (
        <button type="button" disabled className="px-4 py-2 text-sm rounded-lg bg-slate-200 text-slate-600">
          En curso…
        </button>
      );
    }
    if (e === 'sin_datos') {
      return (
        <button
          type="button"
          onClick={() => traerDatosSii(id)}
          className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
        >
          1. Traer datos SII
        </button>
      );
    }
    if (e === 'sin_informe' || e === 'error') {
      return (
        <button
          type="button"
          onClick={() => generarInforme(id, { autoSync: false })}
          className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
        >
          {e === 'error' ? 'Reintentar informe' : '2. Generar informe'}
        </button>
      );
    }
    if (e === 'revision' || e === 'listo') {
      return (
        <button
          type="button"
          onClick={() => verPreview(run._id)}
          className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
        >
          Ver informe
        </button>
      );
    }
    return null;
  };

  const stats = dashboard?.stats;

  const tabCounts = useMemo(() => {
    const base = dashboard?.items || [];
    const counts = { '': base.length };
    for (const t of TAB_FILTROS) {
      if (t.id) counts[t.id] = base.filter((i) => i.estado?.key === t.id).length;
    }
    return counts;
  }, [dashboard]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 pb-24">
      <header className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Informes IVA mensuales</h1>
        <p className="text-slate-600 mt-1 max-w-2xl">
          Para cada cliente y mes: primero traiga ventas/compras del SII, luego genere el informe. El cálculo usa la
          tabla resumen RCV (no contabilidad).
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3 p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
          <span className="text-sm font-medium text-slate-700">Mes a trabajar:</span>
          <select
            className="border rounded-lg px-3 py-2 text-sm"
            value={mes}
            onChange={(e) => setPeriodo((p) => ({ ...p, mes: Number(e.target.value) }))}
          >
            {MESES.map((m, i) =>
              i === 0 ? null : (
                <option key={i} value={i}>
                  {m}
                </option>
              )
            )}
          </select>
          <input
            type="number"
            className="border rounded-lg px-3 py-2 text-sm w-28"
            value={anio}
            onChange={(e) => setPeriodo((p) => ({ ...p, anio: Number(e.target.value) }))}
          />
          <button
            type="button"
            onClick={() => loadDashboard()}
            className="px-4 py-2 text-sm border rounded-lg hover:bg-slate-50"
          >
            Actualizar lista
          </button>
          <div className="ml-auto relative">
            <button
              type="button"
              onClick={() => setAdminOpen((v) => !v)}
              className="px-3 py-2 text-sm text-slate-600 border rounded-lg hover:bg-slate-50"
            >
              Administración ▾
            </button>
            {adminOpen && (
              <div className="absolute right-0 mt-1 z-20 w-48 bg-white border rounded-lg shadow-lg py-1 text-sm">
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-slate-50"
                  onClick={() => {
                    setAdminOpen(false);
                    abrirConfigGlobal();
                  }}
                >
                  Configuración global
                </button>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-slate-50"
                  onClick={async () => {
                    setAdminOpen(false);
                    try {
                      const { data } = await api.get(`/informes-iva/digest/${anio}/${mes}`);
                      setDigest(data);
                    } catch (err) {
                      toast.error(err.response?.data?.error || err.message);
                    }
                  }}
                >
                  Resumen digest
                </button>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-slate-50"
                  onClick={async () => {
                    setAdminOpen(false);
                    try {
                      const { data } = await api.get('/informes-iva/audit?limit=80');
                      setAuditLog(data);
                    } catch (err) {
                      toast.error(err.response?.data?.error || err.message);
                    }
                  }}
                >
                  Auditoría
                </button>
              </div>
            )}
          </div>
        </div>

        <details className="mt-4 text-sm text-slate-600">
          <summary className="cursor-pointer font-medium text-indigo-700">¿Cómo uso esto?</summary>
          <ol className="list-decimal pl-5 mt-2 space-y-1">
            <li>Elija el mes arriba (mismo mes que declarará en el SII).</li>
            <li>Busque la empresa → pulse <strong>Traer datos SII</strong> (solo una vez por mes; tarda varios minutos).</li>
            <li>Cuando diga &quot;Listo para calcular&quot; → pulse <strong>Generar informe</strong> (segundos).</li>
            <li>Revise el informe, apruebe si corresponde y envíe al cliente.</li>
          </ol>
        </details>
      </header>

      <div className="flex flex-wrap gap-2 mb-4">
        {TAB_FILTROS.map((t) => (
          <button
            key={t.id || 'all'}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-full text-sm border ${
              tab === t.id ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-700 border-slate-200'
            }`}
          >
            {t.label}
            {dashboard ? ` (${tabCounts[t.id] ?? 0})` : ''}
          </button>
        ))}
      </div>

      <input
        type="search"
        placeholder="Buscar empresa por nombre, RUT o asignado…"
        className="w-full border rounded-lg px-4 py-2.5 text-sm mb-6"
        value={filtro}
        onChange={(e) => setFiltro(e.target.value)}
      />

      {loading && !dashboard && (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-24 bg-slate-100 animate-pulse rounded-xl" />
          ))}
        </div>
      )}

      <ul className={`space-y-3 ${loading && dashboard ? 'opacity-60' : ''}`}>
        {items.map((row) => {
          const c = row.cliente;
          const est = row.estado || {};
          const run = row.lastRun;
          const style = ESTADO_STYLE[est.key] || ESTADO_STYLE.sin_datos;
          return (
            <li
              key={c._id}
              className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-slate-300 transition-colors"
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="font-semibold text-slate-900 truncate">{c.nombre}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${style}`}>{est.label}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {c.rut}
                    {c.asignado ? ` · ${c.asignado}` : ''}
                  </p>
                  <p className="text-sm text-slate-600 mt-2">{datosResumen(row)}</p>
                  {est.hint && <p className="text-xs text-slate-500 mt-1">{est.hint}</p>}
                  {run?.mes === mes && run?.anio === anio && run?.estimatedVatDetermined != null && (
                    <p className="text-sm font-medium text-slate-900 mt-2">
                      IVA estimado: {fmtMonto(run.estimatedVatDetermined)}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  {primaryButton(row)}
                  <button
                    type="button"
                    onClick={() => abrirDrawer(row)}
                    className="px-3 py-2 text-sm border rounded-lg hover:bg-slate-50"
                  >
                    Más opciones
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {!loading && items.length === 0 && (
        <p className="text-center text-slate-500 py-12">No hay empresas en este filtro.</p>
      )}

      {job && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-5 border-b bg-slate-50">
              <p className="text-xs uppercase tracking-wide text-slate-500">
                {job.kind === 'sync' ? 'Descarga SII' : 'Generación informe IVA'}
              </p>
              <h2 className="text-lg font-semibold text-slate-900 mt-1">{job.nombre}</h2>
              <p className="text-sm text-slate-600 mt-1">
                {job.done
                  ? job.success
                    ? 'Proceso terminado'
                    : 'Proceso con error'
                  : RUN_STATUS_ES[job.status] || job.status || 'En curso…'}
              </p>
              {!job.done && (
                <p className="text-sm font-mono text-indigo-700 mt-2">
                  Tiempo transcurrido: {formatElapsed(job.startedAtMs || clock)}
                </p>
              )}
            </div>

            {!job.done && job.kind === 'run' && (
              <div className="px-5 py-3 border-b flex flex-wrap gap-2 text-xs">
                {PIPELINE_STEPS.map((step, idx) => {
                  const active = idx <= stepIndexForStatus(job.status);
                  return (
                    <span
                      key={step.id}
                      className={`px-2 py-1 rounded-full ${active ? 'bg-indigo-100 text-indigo-800' : 'bg-slate-100 text-slate-400'}`}
                    >
                      {step.label}
                    </span>
                  );
                })}
              </div>
            )}

            <div className="p-5 flex-1 overflow-y-auto min-h-[120px]">
              {!job.done && (
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  El servidor sigue trabajando — no cierre esta pestaña.
                </div>
              )}
              {job.done && job.success && job.runId && (
                <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                  <p className="font-medium text-emerald-900">Informe disponible</p>
                  {job.iva != null && (
                    <p className="text-sm mt-1">IVA estimado: {fmtMonto(job.iva)}</p>
                  )}
                  <button
                    type="button"
                    className="mt-3 w-full py-2.5 bg-emerald-600 text-white rounded-lg font-medium"
                    onClick={() => verPreview(job.runId)}
                  >
                    Abrir informe
                  </button>
                  <button
                    type="button"
                    className="mt-2 w-full py-2 text-sm border rounded-lg"
                    onClick={() => descargarPdf(job.runId)}
                  >
                    Descargar PDF
                  </button>
                </div>
              )}
              {job.done && !job.success && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-900">
                  {job.error || 'Revise el detalle del error.'}
                  {job.runId && (
                    <button
                      type="button"
                      className="mt-2 block underline"
                      onClick={() => verDetalleRun(job.runId)}
                    >
                      Ver qué pasó
                    </button>
                  )}
                </div>
              )}
              <p className="text-xs font-medium text-slate-500 mb-2">Actividad reciente</p>
              <ul className="text-xs font-mono space-y-1 text-slate-700 max-h-48 overflow-y-auto bg-slate-50 rounded-lg p-3 border">
                {(job.logs || []).length === 0 && <li>Esperando respuesta del servidor…</li>}
                {(job.logs || []).slice(-15).map((line, i) => (
                  <li key={`${i}-${line.slice(0, 24)}`}>{line}</li>
                ))}
              </ul>
              {job.runId && !job.done && (
                <p className="text-[10px] text-slate-400 mt-2 break-all">ID seguimiento: {job.runId}</p>
              )}
            </div>

            <div className="p-4 border-t flex justify-end gap-2">
              {job.done ? (
                <button
                  type="button"
                  className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm"
                  onClick={() => setJob(null)}
                >
                  Cerrar
                </button>
              ) : (
                <p className="text-xs text-slate-500 flex-1 self-center">
                  Sync SII suele tardar 5–15 min por empresa.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {drawer && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/30">
          <div className="w-full max-w-md bg-white h-full shadow-xl overflow-y-auto p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-lg font-semibold">{drawer.cliente.nombre}</h2>
                <p className="text-sm text-slate-500">{drawer.cliente.rut}</p>
              </div>
              <button type="button" className="text-slate-500 hover:text-slate-800" onClick={() => setDrawer(null)}>
                ✕
              </button>
            </div>

            <p className="text-sm text-slate-600 mb-6">{datosResumen(drawer)}</p>

            <div className="space-y-2">
              <button
                type="button"
                disabled={busyCliente === drawer.cliente._id}
                onClick={() => traerDatosSii(drawer.cliente._id)}
                className="w-full px-4 py-3 text-left text-sm border rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                <span className="font-medium block">Traer datos del SII</span>
                <span className="text-xs text-slate-500">Ventas, compras y honorarios · lento (5–15 min)</span>
              </button>
              <button
                type="button"
                disabled={busyCliente === drawer.cliente._id}
                onClick={() => generarInforme(drawer.cliente._id, { autoSync: false })}
                className="w-full px-4 py-3 text-left text-sm border rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                <span className="font-medium block">Generar informe IVA</span>
                <span className="text-xs text-slate-500">Rápido si ya hay datos RCV guardados</span>
              </button>
              <button
                type="button"
                disabled={busyCliente === drawer.cliente._id}
                onClick={() => generarInforme(drawer.cliente._id, { autoSync: true })}
                className="w-full px-4 py-3 text-left text-sm border border-amber-200 bg-amber-50 rounded-lg hover:bg-amber-100 disabled:opacity-50"
              >
                <span className="font-medium block">Todo en uno (sync + informe)</span>
                <span className="text-xs text-amber-800">Solo si no quiere dos pasos · muy lento</span>
              </button>
              <button
                type="button"
                disabled={busyCliente === drawer.cliente._id}
                onClick={() => traerDatosSii(drawer.cliente._id, { f29: true })}
                className="w-full px-4 py-2 text-left text-xs border rounded-lg text-slate-600 hover:bg-slate-50"
              >
                Sync incl. propuesta F29 (referencia 89/504)
              </button>
            </div>

            {drawer.lastRun?.mes === mes && drawer.lastRun?.anio === anio && (
              <div className="mt-6 pt-6 border-t space-y-2">
                <p className="text-sm font-medium">Informe de {MESES[mes]} {anio}</p>
                {['READY', 'WAITING_APPROVAL', 'APPROVED', 'SENT'].includes(drawer.lastRun.status) && (
                  <>
                    <button
                      type="button"
                      onClick={() => verPreview(drawer.lastRun._id)}
                      className="w-full py-2 text-sm bg-emerald-600 text-white rounded-lg"
                    >
                      Abrir informe (HTML)
                    </button>
                    {drawer.lastRun.hasPdf !== false && (
                      <button
                        type="button"
                        onClick={() => descargarPdf(drawer.lastRun._id)}
                        className="w-full py-2 text-sm border rounded-lg"
                      >
                        Descargar PDF
                      </button>
                    )}
                    {drawer.lastRun.status === 'WAITING_APPROVAL' && (
                      <button
                        type="button"
                        onClick={() => aprobar(drawer.lastRun._id)}
                        className="w-full py-2 text-sm bg-indigo-600 text-white rounded-lg"
                      >
                        Aprobar informe
                      </button>
                    )}
                    {(drawer.lastRun.status === 'APPROVED' ||
                      (drawer.lastRun.status === 'READY' &&
                        ['HIGH', 'MEDIUM'].includes(drawer.lastRun.confidence))) && (
                      <button
                        type="button"
                        onClick={() => enviar(drawer.lastRun._id)}
                        className="w-full py-2 text-sm bg-amber-600 text-white rounded-lg"
                      >
                        Enviar por email
                      </button>
                    )}
                  </>
                )}
                {['BLOCKED', 'FAILED'].includes(drawer.lastRun.status) && (
                  <button
                    type="button"
                    onClick={() => verDetalleRun(drawer.lastRun._id)}
                    className="w-full py-2 text-sm border border-red-200 text-red-800 rounded-lg"
                  >
                    Ver por qué falló
                  </button>
                )}
              </div>
            )}

            <div className="mt-6 pt-6 border-t">
              <button
                type="button"
                onClick={() => abrirConfig(drawer.cliente._id)}
                className="text-sm text-indigo-700 underline"
              >
                Email del cliente y opciones avanzadas
              </button>
              {historial.length > 0 && (
                <ul className="mt-3 text-xs text-slate-500 space-y-1 max-h-32 overflow-y-auto">
                  {historial.slice(0, 6).map((h) => (
                    <li key={h._id}>
                      {h.period} — {h.status}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {detalleRun && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 text-sm">
            <h2 className="text-lg font-semibold mb-2">Qué pasó</h2>
            {detalleRun.diagnostics?.userMessage && (
              <p className="p-3 bg-red-50 border border-red-100 rounded-lg text-red-900 mb-4">
                {detalleRun.diagnostics.userMessage}
              </p>
            )}
            {detalleRun.diagnostics?.suggestedActions?.length > 0 && (
              <div className="mb-4">
                <p className="font-medium mb-2">Qué puede hacer:</p>
                <ul className="list-disc pl-5 space-y-1 text-slate-700">
                  {detalleRun.diagnostics.suggestedActions.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
              </div>
            )}
            {detalleRun.run?.logEntries?.length > 0 && (
              <details className="mb-4">
                <summary className="cursor-pointer text-slate-600">Log técnico</summary>
                <ul className="mt-2 text-xs font-mono space-y-0.5 max-h-40 overflow-y-auto border rounded p-2 bg-slate-50">
                  {detalleRun.run.logEntries.map((line, idx) => (
                    <li key={idx}>{line}</li>
                  ))}
                </ul>
              </details>
            )}
            <button
              type="button"
              className="w-full py-2 border rounded-lg"
              onClick={() => setDetalleRun(null)}
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {configCliente && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-lg font-semibold mb-4">Cliente — envío de informe</h2>
            <p className="text-xs text-slate-500 mb-3">
              Abra desde la lista: <strong>Más opciones → Email del cliente y opciones avanzadas</strong>
            </p>
            <label className="block text-sm mb-1">Email donde se envía el informe</label>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm mb-4"
              value={configCliente.emailCliente || ''}
              onChange={(e) => setConfigCliente({ ...configCliente, emailCliente: e.target.value })}
            />
            <label className="flex items-center gap-2 text-sm mb-4">
              <input
                type="checkbox"
                checked={Boolean(configCliente.activoInformes)}
                onChange={(e) => setConfigCliente({ ...configCliente, activoInformes: e.target.checked })}
              />
              Incluir en informes IVA
            </label>
            <label className="block text-sm mb-1">Remanente manual (cód. 504), en pesos</label>
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
              placeholder="Vacío = automático"
              value={configCliente.remanenteManual ?? ''}
              onChange={(e) =>
                setConfigCliente({
                  ...configCliente,
                  remanenteManual: e.target.value === '' ? null : Number(e.target.value)
                })
              }
            />
            <label className="block text-sm mb-1">PPM — porcentaje sobre ventas netas RCV</label>
            <input
              type="number"
              step="0.01"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-1"
              placeholder="Ej. 1 = 1%"
              value={configCliente.ppmPorcentaje ?? ''}
              onChange={(e) =>
                setConfigCliente({
                  ...configCliente,
                  ppmPorcentaje: e.target.value === '' ? null : Number(e.target.value)
                })
              }
            />
            <p className="text-xs text-slate-500 mb-3">
              Aparece en el informe como PPM estimado. Si prefiere un monto fijo, use el campo siguiente (tiene prioridad).
            </p>
            <label className="block text-sm mb-1">PPM — monto fijo manual (opcional)</label>
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-4"
              placeholder="Pesos"
              value={configCliente.ppmMontoManual ?? ''}
              onChange={(e) =>
                setConfigCliente({
                  ...configCliente,
                  ppmMontoManual: e.target.value === '' ? null : Number(e.target.value)
                })
              }
            />
            <label className="block text-sm mb-1">Nota en el informe (opcional)</label>
            <textarea
              className="w-full border rounded-lg px-3 py-2 text-sm mb-4 min-h-[60px]"
              value={configCliente.notaContador || ''}
              onChange={(e) => setConfigCliente({ ...configCliente, notaContador: e.target.value })}
            />
            <div className="flex justify-end gap-2">
              <button type="button" className="px-4 py-2 text-sm border rounded-lg" onClick={() => setConfigCliente(null)}>
                Cerrar
              </button>
              <button type="button" className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg" onClick={guardarConfig}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {globalConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 text-sm">
            <h2 className="text-lg font-semibold mb-4">Configuración global</h2>
            <label className="flex items-center gap-2 mb-3">
              <input
                type="checkbox"
                checked={globalConfig.autoSyncOnRun !== false}
                onChange={(e) => setGlobalConfig({ ...globalConfig, autoSyncOnRun: e.target.checked })}
              />
              Sync automático al usar &quot;Todo en uno&quot; / runs programados
            </label>
            <label className="block mb-1">Frescura datos (horas)</label>
            <input
              type="number"
              className="w-full border rounded-lg px-3 py-2 mb-4"
              value={globalConfig.frescuraHoras ?? 48}
              onChange={(e) => setGlobalConfig({ ...globalConfig, frescuraHoras: Number(e.target.value) })}
            />
            <details className="mb-4">
              <summary className="cursor-pointer text-sm font-medium">SMTP envíos</summary>
              <input
                className="w-full border rounded-lg px-3 py-2 mt-2 mb-2"
                placeholder="Host SMTP"
                value={globalConfig.smtpHost || ''}
                onChange={(e) => setGlobalConfig({ ...globalConfig, smtpHost: e.target.value })}
              />
              <input
                type="number"
                className="w-full border rounded-lg px-3 py-2 mb-2"
                placeholder="Puerto"
                value={globalConfig.smtpPort ?? 587}
                onChange={(e) => setGlobalConfig({ ...globalConfig, smtpPort: Number(e.target.value) })}
              />
              <input
                className="w-full border rounded-lg px-3 py-2 mb-2"
                placeholder="Usuario"
                value={globalConfig.smtpUser || ''}
                onChange={(e) => setGlobalConfig({ ...globalConfig, smtpUser: e.target.value })}
              />
              <input
                className="w-full border rounded-lg px-3 py-2 mb-2"
                placeholder="From"
                value={globalConfig.smtpFrom || ''}
                onChange={(e) => setGlobalConfig({ ...globalConfig, smtpFrom: e.target.value })}
              />
              <input
                type="password"
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Contraseña"
                value={globalConfig.smtpPass === '********' ? '' : globalConfig.smtpPass || ''}
                onChange={(e) => setGlobalConfig({ ...globalConfig, smtpPass: e.target.value || '********' })}
              />
            </details>
            <div className="flex justify-end gap-2">
              <button type="button" className="px-4 py-2 border rounded-lg" onClick={() => setGlobalConfig(null)}>
                Cerrar
              </button>
              <button type="button" className="px-4 py-2 bg-indigo-600 text-white rounded-lg" onClick={guardarConfigGlobal}>
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {digest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 text-sm max-h-[80vh] overflow-y-auto">
            <h2 className="font-semibold mb-2">Digest {digest.mes}/{digest.anio}</h2>
            <pre className="text-xs bg-slate-50 p-2 rounded overflow-auto">{JSON.stringify(digest.resumen, null, 2)}</pre>
            <button type="button" className="mt-4 w-full py-2 border rounded-lg" onClick={() => setDigest(null)}>
              Cerrar
            </button>
          </div>
        </div>
      )}

      {auditLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 text-xs max-h-[80vh] overflow-y-auto">
            <h2 className="font-semibold mb-2 text-sm">Auditoría</h2>
            <ul className="space-y-1">
              {auditLog.map((e) => (
                <li key={e._id}>
                  {new Date(e.createdAt).toLocaleString('es-CL')} — {e.action}
                </li>
              ))}
            </ul>
            <button type="button" className="mt-4 w-full py-2 border rounded-lg text-sm" onClick={() => setAuditLog(null)}>
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
